import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { ApiError } from '@/common/services/api/types';
import { USER_TYPE } from '@/common/constants';
import { useUserStore } from './userStore';

// ── Carte 1 : Infos navigateur / requête ──
export interface BrowserErrorInfo {
  statusCode?: number;
  errorType?: string;
  stack?: string;
  userAgent?: string;
  timestamp?: string;
}

// ── Carte 2 : Erreur interne (ce que l'API retourne) ──
export interface InnerErrorInfo {
  message?: string;
  innerExceptionMessage?: string;
  code?: string;
  appData?: Record<string, unknown>;
  rawBody?: string;
}

// ── Carte 3 : Message personnalisé (ce que le dev décide d'afficher) ──
export interface PersonalErrorInfo {
  context?: string;
  requestType?: string;
  resourceId?: string | number;
  message?: string;
}

// ── État complet stocké dans le store ──
export interface GlobalErrorDetails {
  browser: BrowserErrorInfo;
  inner: InnerErrorInfo;
  personal: PersonalErrorInfo;
}

const LOG_PREFIX = '[GlobalErrorModal]';

/**
 * Extrait les infos navigateur et erreur interne depuis une erreur brute.
 * Compatible avec ApiError (fetch), AxiosError, TypeError, Error standard.
 */
const extractFromError = (error: unknown): { browser: BrowserErrorInfo; inner: InnerErrorInfo } => {
  const browser: BrowserErrorInfo = {
    timestamp: new Date().toISOString(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };
  const inner: InnerErrorInfo = {};

  if (!error || typeof error !== 'object') {
    return { browser, inner };
  }

  // TypeError (erreur réseau, etc.)
  if (error instanceof TypeError) {
    browser.statusCode = 500;
    browser.errorType = 'TypeError';
    browser.stack = error.stack;
    inner.message = error.message;
    return { browser, inner };
  }

  // ApiError (ApiClientBase / fetch) → { status, message, code? }
  const apiErr = error as ApiError;
  if (typeof apiErr.status === 'number') {
    browser.statusCode = apiErr.status;
    browser.errorType = 'ApiError';
    inner.code = apiErr.code;

    try {
      const parsed = JSON.parse(apiErr.message);
      if (parsed && typeof parsed === 'object') {
        inner.message = typeof parsed.message === 'string' ? parsed.message : apiErr.message;
        inner.innerExceptionMessage =
          typeof parsed.InnerExceptionMessage === 'string'
            ? parsed.InnerExceptionMessage
            : undefined;
        // Toutes les autres clés → appData
        const { message: _m, InnerExceptionMessage: _i, ...rest } = parsed;
        if (Object.keys(rest).length > 0) {
          inner.appData = rest;
        }
      }
      inner.rawBody = apiErr.message;
    } catch {
      inner.message = apiErr.message;
    }

    return { browser, inner };
  }

  // AxiosError shape → { response: { status, data }, config?: { url, method } }
  const axiosLike = error as {
    response?: { status?: number; data?: unknown };
    config?: { url?: string; method?: string };
  };
  if (axiosLike.response && typeof axiosLike.response.status === 'number') {
    browser.statusCode = axiosLike.response.status;
    browser.errorType = 'AxiosError';

    const data = axiosLike.response.data;
    if (typeof data === 'string') {
      inner.message = data;
      inner.rawBody = data;
    } else if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      inner.message = typeof obj.message === 'string' ? obj.message : undefined;
      inner.innerExceptionMessage =
        typeof obj.InnerExceptionMessage === 'string' ? obj.InnerExceptionMessage : undefined;
      const { message: _m, InnerExceptionMessage: _i, ...rest } = obj;
      if (Object.keys(rest).length > 0) {
        inner.appData = rest;
      }
      inner.rawBody = JSON.stringify(data);
    }

    return { browser, inner };
  }

  // Error standard (SyntaxError, RangeError, etc.)
  if (error instanceof Error) {
    browser.errorType = error.constructor.name;
    browser.stack = error.stack;
    inner.message = error.message;
    return { browser, inner };
  }

  // Fallback inconnu
  inner.rawBody = String(error);
  return { browser, inner };
};

/**
 * Détecte si les DevTools du navigateur sont ouverts via l'heuristique de taille de fenêtre.
 */
const isDevToolsOpen = (): boolean => {
  if (typeof window === 'undefined') return false;
  const threshold = 160;
  return (
    window.outerWidth - window.innerWidth > threshold ||
    window.outerHeight - window.innerHeight > threshold
  );
};

/**
 * Vérifie si l'erreur est liée à une session expirée (token refresh null).
 * Dans ce cas, on ne veut pas afficher la modal d'erreur.
 */
const isSessionExpiredError = (error: unknown): boolean => {
  if (error instanceof Error && error.message.includes('Token refresh returned null')) {
    return true;
  }
  return false;
};

const emptyDetails = (): GlobalErrorDetails => ({
  browser: {},
  inner: {},
  personal: {},
});

export const useGlobalErrorModalStore = defineStore('globalErrorModal', () => {
  // State
  const isOpen = ref(false);
  const details = ref<GlobalErrorDetails>(emptyDetails());

  // Timer interne pour la modal auto (ApiClientBase)
  let pendingAutoTimer: ReturnType<typeof setTimeout> | null = null;

  /** Vrai si l'utilisateur courant est un étudiant. */
  const isStudent = computed(() => {
    const userStore = useUserStore();
    return userStore.userType === USER_TYPE.STUDENT;
  });

  // Actions

  /**
   * Construit les details à partir des infos personnalisées + erreur brute.
   */
  const buildDetails = (personalInfo?: PersonalErrorInfo, error?: unknown): GlobalErrorDetails => {
    const { browser, inner } = extractFromError(error);
    const result: GlobalErrorDetails = {
      browser,
      inner,
      personal: personalInfo ?? {},
    };

    return result;
  };

  /**
   * Affiche la modal d'erreur avec un contexte personnalisé.
   * Annule automatiquement toute modal auto en attente.
   */
  const showError = (personalInfo?: PersonalErrorInfo, error?: unknown) => {
    // Annuler la modal auto si un composant gère l'erreur lui-même
    if (pendingAutoTimer) {
      clearTimeout(pendingAutoTimer);
      pendingAutoTimer = null;
    }

    // Ne pas afficher la modal pour les erreurs de session expirée
    if (isSessionExpiredError(error)) {
      return;
    }

    // Ne pas afficher la modal si les DevTools sont ouverts
    if (isDevToolsOpen()) {
      return;
    }

    details.value = buildDetails(personalInfo, error);
    isOpen.value = true;
  };

  /**
   * Planifie une modal d'erreur par défaut (infos brutes).
   * Utilisé par ApiClientBase. Si un composant appelle showError() avant
   * que le timer ne s'exécute, la modal auto est annulée.
   */
  const scheduleAutoError = (personalInfo?: PersonalErrorInfo, error?: unknown) => {
    // Ne pas planifier la modal pour les erreurs de session expirée
    if (isSessionExpiredError(error)) {
      return;
    }

    if (pendingAutoTimer) {
      clearTimeout(pendingAutoTimer);
    }

    pendingAutoTimer = setTimeout(() => {
      pendingAutoTimer = null;
      if (!isOpen.value && !isDevToolsOpen()) {
        details.value = buildDetails(personalInfo, error);
        isOpen.value = true;
      }
    }, 0);
  };

  const closeError = () => {
    isOpen.value = false;
    details.value = emptyDetails();
  };

  return {
    // State
    isOpen,
    details,

    // Getters
    isStudent,

    // Actions
    showError,
    scheduleAutoError,
    closeError,
  };
});
